import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActiveCustomer, AgentCustomerAnalysis } from '../../../types/organizations';
import { authFetch } from '../../../utils/authFetch';
import { useUser } from '../../../context/UserContext';
import { customerService } from '../../../services/customerService';
import { organizationService } from '../../../services/organizationService';
import { conversationService } from '../../../services/conversationService';
import { notesService } from '../../../services/notesService';
import CustomerReasoningModal from '../../common/CustomerReasoningModal';
import CustomerInvoicesModal from '../../common/CustomerInvoicesModal';
import NotesModal from '../../common/modals/NotesModal';
import CustomerCard from '../cards/CustomerCard';
import APArchetypesAssignmentModal from '../modals/APArchetypesAssignmentModal';
import GovernanceOverlaysAssignmentModal from '../modals/GovernanceOverlaysAssignmentModal';
import { CustomerCreditTermsLimitModal } from '../modals';
import PointOfContactInputModal from '../modals/PointOfContactInputModal';
import DisconnectConfirmationModal from '../modals/DisconnectConfirmationModal';
import { getCategoryStyling } from '../../../utils/categoryStyling';
import { getLatestAnalysis } from '../../../utils/analysis';
import { getCustomerPhoneData } from '../../../utils/customerPhoneUtils';

interface CustomersTabProps {
  organizationId: string;
  accessToken: string | null;
  setUserAndToken: (user: any, token: string) => void;
  onCustomerNoteCountChange?: (customerId: string, delta: number) => void;
}

// use shared getLatestAnalysis from utils/analysis


// Helper function to get financial risk score
const getFinancialRiskScore = (customer: ActiveCustomer): { score: number; confidence: string } | null => {
  const analysis = getLatestAnalysis(customer.agent_analyses, 'financial_risk_score');
  if (!analysis?.output_json) return null;

  const score = typeof analysis.output_json.score === 'string'
    ? parseInt(analysis.output_json.score) || 0
    : analysis.output_json.score || 0;
  const confidence = analysis.output_json.confidence || 'Unknown';

  // Convert confidence to shorter form
  const shortConfidence = confidence === 'Medium' ? 'Mid' : confidence;

  return {
    score,
    confidence: shortConfidence
  };
};


// Helper function to get customer insight analysis
const getCustomerInsightAnalysis = (customer: ActiveCustomer): AgentCustomerAnalysis | null => {
  return getLatestAnalysis(customer.agent_analyses, 'customer_insight');
};

// Helper function to get customer insight category
const getCustomerInsightCategory = (customer: ActiveCustomer): string | null => {
  const insight = getLatestAnalysis(customer.agent_analyses, 'customer_insight');
  return insight?.output_json?.selected_category || null;
};

// Helper function to get customer profile summary
const getCustomerProfileSummary = (customer: ActiveCustomer): string | null => {
  const insight = getLatestAnalysis(customer.agent_analyses, 'customer_insight');
  return insight?.output_json?.customer_profile_summary || null;
};

// Helper function to truncate text to approximately 3 lines
const truncateToThreeLines = (text: string, maxLength: number = 80): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};



const CustomersTab: React.FC<CustomersTabProps> = ({ organizationId, accessToken, setUserAndToken, onCustomerNoteCountChange }) => {
  const navigate = useNavigate();
  const [analyzingCustomers, setAnalyzingCustomers] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ActiveCustomer | null>(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState<AgentCustomerAnalysis | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false);
  const [selectedCustomerForInvoices, setSelectedCustomerForInvoices] = useState<ActiveCustomer | null>(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditModalCustomer, setCreditModalCustomer] = useState<ActiveCustomer | null>(null);
  const [showCreditViewModal, setShowCreditViewModal] = useState(false);
  const [creditViewCustomer, setCreditViewCustomer] = useState<ActiveCustomer | null>(null);
  const [creditViewInitialValues, setCreditViewInitialValues] = useState<{ credit_terms?: string | null; credit_terms_desc?: string | null; credit_limit?: number | null; credit_limit_desc?: string | null; hasIndividualized?: boolean } | null>(null);
  const [customerIdToHasIndividualized, setCustomerIdToHasIndividualized] = useState<Record<string, boolean>>({});
  const [invoicesCache, setInvoicesCache] = useState<Record<string, any[]>>({});
  const [reAnalyzingCustomers, setReAnalyzingCustomers] = useState<Set<string>>(new Set());
  const [reAnalyzeJobIds, setReAnalyzeJobIds] = useState<Record<string, string>>({});
  const [reAnalyzeJobStatuses, setReAnalyzeJobStatuses] = useState<Record<string, string>>({});
  
  // Point of contact input modal state
  const [isPointOfContactModalOpen, setIsPointOfContactModalOpen] = useState(false);
  const [selectedCustomerForConversation, setSelectedCustomerForConversation] = useState<ActiveCustomer | null>(null);
  const [conversationChannel, setConversationChannel] = useState<'whatsapp' | 'email'>('whatsapp');
  const [picName, setPicName] = useState('');
  const [picPhoneNumber, setPicPhoneNumber] = useState('');
  const [picEmail, setPicEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+65');
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState('');
  const [existingConversations, setExistingConversations] = useState<any[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [useExistingConversation, setUseExistingConversation] = useState(true);
  
  // Notes modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedCustomerForNotes, setSelectedCustomerForNotes] = useState<ActiveCustomer | null>(null);
  const [customerNotesCount, setCustomerNotesCount] = useState<Record<string, number>>({});
  
  // AP Archetypes modal state
  const [selectedCustomerForAPArchetypes, setSelectedCustomerForAPArchetypes] = useState<ActiveCustomer | null>(null);
  
  // Governance Overlays modal state
  const [selectedCustomerForGovernanceOverlays, setSelectedCustomerForGovernanceOverlays] = useState<ActiveCustomer | null>(null);
  
  // Assignment modals state
  const [showAPArchetypesAssignmentModal, setShowAPArchetypesAssignmentModal] = useState(false);
  const [showGovernanceOverlaysAssignmentModal, setShowGovernanceOverlaysAssignmentModal] = useState(false);
  const [availableAPArchetypes, setAvailableAPArchetypes] = useState<any[]>([]);
  const [availableGovernanceOverlays, setAvailableGovernanceOverlays] = useState<any[]>([]);
  const [selectedAPArchetypes, setSelectedAPArchetypes] = useState<string[]>([]);
  const [selectedGovernanceOverlays, setSelectedGovernanceOverlays] = useState<string[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  
  // Disconnect confirmation modal state
  const [showDisconnectConfirmModal, setShowDisconnectConfirmModal] = useState(false);
  const [disconnectItem, setDisconnectItem] = useState<{
    type: 'ap_archetype' | 'governance_overlay';
    id: string;
    name: string;
    customerId: string;
  } | null>(null);

  // Customers data fetching and caching state
  const [allCustomers, setAllCustomers] = useState<ActiveCustomer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<ActiveCustomer[]>([]);
  const [displayedCustomers, setDisplayedCustomers] = useState<ActiveCustomer[]>([]);
  const [displayCount, setDisplayCount] = useState(20); // Number of customers to display
  const CUSTOMERS_PAGE_SIZE = 20;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleOpenReasoningModal = (customer: ActiveCustomer) => {
    const analysis = getCustomerInsightAnalysis(customer);
    if (analysis) {
      setSelectedCustomer(customer);
      setSelectedAnalysis(analysis);
      setIsModalOpen(true);
    }
  };

  const handleCloseReasoningModal = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
    setSelectedAnalysis(null);
  };

  const handleOpenInvoicesModal = async (customer: ActiveCustomer) => {
    // Add extracted properties to the customer object
    const selectedCategory = getCustomerInsightCategory(customer);
    const profileSummary = getCustomerProfileSummary(customer);

    const customerWithExtractedData = {
      ...customer,
      selected_category: selectedCategory || undefined,
      customer_profile_summary: profileSummary || undefined
    };

    // Check if invoices are already cached
    if (!invoicesCache[customer.xero_contact_id]) {
      try {
        const result = await customerService.getCustomerInvoices(
          organizationId,
          customer.xero_contact_id,
          accessToken,
          setUserAndToken
        );
        setInvoicesCache(prev => ({
          ...prev,
          [customer.xero_contact_id]: result.invoices || []
        }));
      } catch (err: any) {
        console.error('Error fetching invoices:', err);
        // Still open modal even if fetch fails
      }
    }

    setSelectedCustomerForInvoices(customerWithExtractedData);
    setIsInvoicesModalOpen(true);
  };

  const handleCloseInvoicesModal = () => {
    setIsInvoicesModalOpen(false);
    setSelectedCustomerForInvoices(null);
  };

  const handleOpenCustomerCreditModal = (customer: ActiveCustomer) => {
    setCreditModalCustomer(customer);
    setShowCreditModal(true);
  };

  const hasIndividualizedCredit = (customerId: string) => Boolean(customerIdToHasIndividualized[customerId]);

  const handleOpenCustomerCreditView = (customer: ActiveCustomer, initialValues: { credit_terms?: string | null; credit_terms_desc?: string | null; credit_limit?: number | null; credit_limit_desc?: string | null; hasIndividualized?: boolean }) => {
    setCreditViewCustomer(customer);
    setCreditViewInitialValues(initialValues);
    setShowCreditViewModal(true);
  };

  // Hydrate icon color state from customers prop whenever it changes
  useEffect(() => {
    const nextMap: Record<string, boolean> = {};
    allCustomers.forEach((c: ActiveCustomer) => {
      const rec: any = (c as any).individualized_credit;
      nextMap[c.id] = Boolean(rec && rec.status === 'active');
    });
    setCustomerIdToHasIndividualized(nextMap);
  }, [allCustomers]);

  const handleOpenPointOfContactModal = async (customer: ActiveCustomer) => {
    setSelectedCustomerForConversation(customer);
    setConversationError('');
    // Default to WhatsApp, but user can change in modal
    setConversationChannel('whatsapp');

    // Check if there are existing conversations for this customer
    const existingConversationsList = await checkExistingConversation(customer.id);
    
    // Always show modal for conversation selection, even if only one conversation exists
    if (existingConversationsList.length > 0) {
      // Show modal for selection (user can choose which conversation or create new)
      setExistingConversations(existingConversationsList);
      // Pre-select first conversation (prioritize WhatsApp, then email, then any)
      const conversationsWithWhatsApp = existingConversationsList.filter((conv: any) => conv?.pic_contact?.whatsapp);
      const conversationsWithEmail = existingConversationsList.filter((conv: any) => conv?.pic_contact?.email);
      const defaultConv = conversationsWithWhatsApp[0] || conversationsWithEmail[0] || existingConversationsList[0];
      setSelectedConversationId(defaultConv?.id || null);
      setUseExistingConversation(true);
      setPicName(defaultConv?.pic_name || customer.name);
      
      // Prefill based on what the conversation has
      if (defaultConv?.pic_contact?.whatsapp) {
        setCountryCode('+65'); // Default
        setPicPhoneNumber(''); // Modal will show it as uneditable from selectedConversation
      } else {
        const phoneData = getCustomerPhoneData(customer);
        setPicPhoneNumber(phoneData?.phoneNumber || '');
        setCountryCode('+65');
      }
      
      if (defaultConv?.pic_contact?.email) {
        setPicEmail(defaultConv.pic_contact.email);
      } else {
        setPicEmail(customer.email_address || '');
      }
      
      setIsPointOfContactModalOpen(true);
      return;
    }

    // No existing conversations - show modal with defaults
    const phoneData = getCustomerPhoneData(customer);
    setPicName('');
    setPicPhoneNumber(phoneData?.phoneNumber || '');
    setPicEmail(customer.email_address || '');
    setCountryCode('+65');
    setExistingConversations([]);
    setSelectedConversationId(null);
    setUseExistingConversation(true);
    setIsPointOfContactModalOpen(true);
  };

  const handleClosePointOfContactModal = () => {
    setIsPointOfContactModalOpen(false);
    setSelectedCustomerForConversation(null);
    setConversationChannel('whatsapp');
    setPicName('');
    setPicPhoneNumber('');
    setPicEmail('');
    setCountryCode('+65');
    setConversationError('');
    setExistingConversations([]);
    setSelectedConversationId(null);
    setUseExistingConversation(true);
  };

  const handleStartConversation = async () => {
    if (!selectedCustomerForConversation) return;

    setConversationLoading(true);
    setConversationError('');

    try {
      if (!accessToken) {
        setConversationError('Authentication required');
        return;
      }

      if (!picName.trim()) {
        setConversationError('Please enter the Point-of-Contact name');
        return;
      }

      const picNameToUse = picName.trim();
      let conversationId: string;
      const channel = conversationChannel;

      // Validate channel-specific fields
      if (channel === 'whatsapp') {
        if (!picPhoneNumber.trim()) {
          setConversationError('Please enter a WhatsApp number');
          return;
        }
      } else if (channel === 'email') {
        if (!picEmail.trim()) {
          setConversationError('Please enter an email address');
          return;
        }
      }

      // Handle conversation creation/update
      if (useExistingConversation && selectedConversationId) {
        // Find the selected conversation
        const selectedConversation = existingConversations.find((conv: any) => conv.id === selectedConversationId);
        if (!selectedConversation) {
          setConversationError('Selected conversation not found');
          return;
        }
        
        // Update existing conversation - merge new channel into existing pic_contact
        conversationId = selectedConversation.id;
        
        // Merge new channel into existing pic_contact (preserve other channels)
        const existingPicContact = selectedConversation.pic_contact || {};
        const updatedPicContact = {
          ...existingPicContact,
        };

        if (channel === 'whatsapp') {
          const picContact = `${countryCode}${picPhoneNumber}`;
          updatedPicContact.whatsapp = picContact;
        } else if (channel === 'email') {
          updatedPicContact.email = picEmail.trim();
        }
        
        // Update the existing conversation to add the channel to pic_contact
        const response = await authFetch(
          `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              organization_id: organizationId,
              pic_contact: updatedPicContact, // Merge with existing contacts
              pic_name: picNameToUse,
              channel: channel,
              xero_direct_contact_id: selectedCustomerForConversation.id
            })
          },
          accessToken,
          setUserAndToken
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setConversationError(errorData.detail || 'Failed to update conversation');
          return;
        }

        const updatedConversation = await response.json();
        conversationId = updatedConversation.id;
      } else {
        // Create new conversation (user selected "Create new conversation")
        // Pass force_new flag to create a new conversation even if one exists for this customer
        const picContact: any = {};
        
        if (channel === 'whatsapp') {
          const picContactPhone = `${countryCode}${picPhoneNumber}`;
          picContact.whatsapp = picContactPhone;
        } else if (channel === 'email') {
          picContact.email = picEmail.trim();
        }

        const response = await authFetch(
          `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              organization_id: organizationId,
              pic_contact: picContact, // Use dict format to match backend expectations
              pic_name: picNameToUse,
              channel: channel,
              xero_direct_contact_id: selectedCustomerForConversation.id,
              force_new: true  // Force creation of new conversation
            })
          },
          accessToken,
          setUserAndToken
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setConversationError(errorData.detail || 'Failed to create conversation');
          return;
        }

        const conversation = await response.json();
        conversationId = conversation.id;
      }

      // Navigate to conversation manager (currently both use whatsapp-manager)
      // TODO: Update when email-manager route is available
      navigate(`/whatsapp-manager?conversation=${conversationId}`);
    } catch (err: any) {
      setConversationError(err.message || 'Failed to start conversation');
    } finally {
      setConversationLoading(false);
    }
  };

  const checkExistingConversation = async (customerId: string) => {
    try {
      // Use the new endpoint that looks up by xero_direct_contact_id
      // This now returns a list of all conversations for this customer
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations/xero_direct_contact_id/${customerId}`,
        {},
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const conversations = await response.json();
        // Return the list of conversations (can be empty array)
        return Array.isArray(conversations) ? conversations : [];
      }
      // 404 or other error means no conversations found for this xero_direct_contact_id
      return [];
    } catch (err) {
      console.error('Error checking existing conversation:', err);
      return [];
    }
  };


  const handleOpenNotesModal = async (customer: ActiveCustomer) => {
    setSelectedCustomerForNotes(customer);
    setShowNotesModal(true);
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setSelectedCustomerForNotes(null);
  };

  const handleOpenAPArchetypesModal = async (customer: ActiveCustomer) => {
    setSelectedCustomerForAPArchetypes(customer);
    
    // Always show assignment modal to add new connections
    handleAssignAPArchetypes(customer);
  };

  const handleOpenGovernanceOverlaysModal = async (customer: ActiveCustomer) => {
    setSelectedCustomerForGovernanceOverlays(customer);
    
    // Always show assignment modal to add new connections
    handleAssignGovernanceOverlays(customer);
  };

  const handleDisconnectAPArchetype = async (archetypeId: string, customerId: string) => {
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/organizations/${organizationId}/customers/${customerId}/ap-archetypes/disconnect`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ap_archetype_ids: [archetypeId] })
        },
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        // Refresh customer data
        fetchCustomers(true);
      } else {
        console.error('Failed to disconnect AP archetype');
      }
    } catch (err: any) {
      console.error('Failed to disconnect AP archetype:', err);
    }
  };

  const handleConfirmDisconnectAPArchetype = (archetypeId: string, archetypeName: string, customerId: string) => {
    setDisconnectItem({
      type: 'ap_archetype',
      id: archetypeId,
      name: archetypeName,
      customerId: customerId
    });
    setShowDisconnectConfirmModal(true);
  };

  const handleDisconnectGovernanceOverlay = async (overlayId: string, customerId: string) => {
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/organizations/${organizationId}/customers/${customerId}/governance-overlays/disconnect`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ governance_overlay_ids: [overlayId] })
        },
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        // Refresh customer data
        fetchCustomers(true);
      } else {
        console.error('Failed to disconnect governance overlay');
      }
    } catch (err: any) {
      console.error('Failed to disconnect governance overlay:', err);
    }
  };

  const handleConfirmDisconnectGovernanceOverlay = (overlayId: string, overlayName: string, customerId: string) => {
    setDisconnectItem({
      type: 'governance_overlay',
      id: overlayId,
      name: overlayName,
      customerId: customerId
    });
    setShowDisconnectConfirmModal(true);
  };

  const handleAssignAPArchetypes = async (customer: ActiveCustomer) => {
    try {
      setAssignmentLoading(true);
      setAssignmentError('');
      
      // Fetch available AP archetypes connected to this organization
      const archetypes = await organizationService.getOrganizationAPArchetypes(organizationId, accessToken, setUserAndToken);
      
      // Filter out already connected archetypes
      const connectedIds = customer.ap_archetypes?.map(a => a.id) || [];
      const unconnectedArchetypes = archetypes.filter(archetype => !connectedIds.includes(archetype.id));
      
      setAvailableAPArchetypes(unconnectedArchetypes);
      setSelectedAPArchetypes([]); // Start with empty selection for new assignments
      
      // Open assignment modal
      setShowAPArchetypesAssignmentModal(true);
    } catch (err: any) {
      setAssignmentError(err.message || 'Failed to load AP archetypes');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAssignGovernanceOverlays = async (customer: ActiveCustomer) => {
    try {
      setAssignmentLoading(true);
      setAssignmentError('');
      
      // Fetch available governance overlays connected to this organization
      const overlays = await organizationService.getOrganizationGovernanceOverlays(organizationId, accessToken, setUserAndToken);
      
      // Filter out already connected overlays
      const connectedIds = customer.governance_overlays?.map(g => g.id) || [];
      const unconnectedOverlays = overlays.filter(overlay => !connectedIds.includes(overlay.id));
      
      setAvailableGovernanceOverlays(unconnectedOverlays);
      setSelectedGovernanceOverlays([]); // Start with empty selection for new assignments
      
      // Open assignment modal
      setShowGovernanceOverlaysAssignmentModal(true);
    } catch (err: any) {
      setAssignmentError(err.message || 'Failed to load governance overlays');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleCloseAPArchetypesAssignmentModal = () => {
    setShowAPArchetypesAssignmentModal(false);
    setSelectedCustomerForAPArchetypes(null);
    setSelectedAPArchetypes([]);
    setAssignmentError('');
  };

  const handleCloseGovernanceOverlaysAssignmentModal = () => {
    setShowGovernanceOverlaysAssignmentModal(false);
    setSelectedCustomerForGovernanceOverlays(null);
    setSelectedGovernanceOverlays([]);
    setAssignmentError('');
  };

  const handleSubmitAPArchetypesAssignment = async () => {
    if (!selectedCustomerForAPArchetypes) return;
    
    try {
      setAssignmentLoading(true);
      setAssignmentError('');
      
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/organizations/${organizationId}/customers/${selectedCustomerForAPArchetypes.id}/ap-archetypes/connect`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            ap_archetype_ids: selectedAPArchetypes
          })
        },
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        // Refresh customer data
        fetchCustomers(true);
        // Close modal
        handleCloseAPArchetypesAssignmentModal();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setAssignmentError(errorData.detail || 'Failed to assign AP archetypes');
      }
    } catch (err: any) {
      setAssignmentError(err.message || 'Failed to assign AP archetypes');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleSubmitGovernanceOverlaysAssignment = async () => {
    if (!selectedCustomerForGovernanceOverlays) return;
    
    try {
      setAssignmentLoading(true);
      setAssignmentError('');
      
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/organizations/${organizationId}/customers/${selectedCustomerForGovernanceOverlays.id}/governance-overlays/connect`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            governance_overlay_ids: selectedGovernanceOverlays
          })
        },
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        // Refresh customer data
        fetchCustomers(true);
        // Close modal
        handleCloseGovernanceOverlaysAssignmentModal();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setAssignmentError(errorData.detail || 'Failed to assign governance overlays');
      }
    } catch (err: any) {
      setAssignmentError(err.message || 'Failed to assign governance overlays');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAPArchetypeToggle = (archetypeId: string) => {
    setSelectedAPArchetypes(prev => 
      prev.includes(archetypeId) 
        ? prev.filter(id => id !== archetypeId)
        : [...prev, archetypeId]
    );
  };

  const handleGovernanceOverlayToggle = (overlayId: string) => {
    setSelectedGovernanceOverlays(prev => 
      prev.includes(overlayId) 
        ? prev.filter(id => id !== overlayId)
        : [...prev, overlayId]
    );
  };

  const handleCloseDisconnectConfirmModal = () => {
    setShowDisconnectConfirmModal(false);
    setDisconnectItem(null);
  };

  const handleConfirmDisconnect = async () => {
    if (!disconnectItem) return;

    if (disconnectItem.type === 'ap_archetype') {
      await handleDisconnectAPArchetype(disconnectItem.id, disconnectItem.customerId);
    } else if (disconnectItem.type === 'governance_overlay') {
      await handleDisconnectGovernanceOverlay(disconnectItem.id, disconnectItem.customerId);
    }

    handleCloseDisconnectConfirmModal();
  };

  // Fetch customers with caching (similar to CustomersPSETab)
  const fetchCustomers = async (forceRefresh: boolean = false) => {
    if (!organizationId || !accessToken) return;

    // Check if we have cached data (unless force refresh is requested)
    const cacheKey = `customers_all_${organizationId}`;
    const cachedData = localStorage.getItem(cacheKey);
    const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
    
    // Check if cache is still valid (60 minutes)
    const now = Date.now();
    const cacheAge = cacheTimestamp ? now - parseInt(cacheTimestamp) : Infinity;
    const isCacheValid = cacheAge < 60 * 60 * 1000; // 60 minutes
    
    if (!forceRefresh && cachedData && isCacheValid) {
      try {
        const parsedData = JSON.parse(cachedData);
        setAllCustomers(parsedData);
        setError('');
        setLoading(false);
        return;
      } catch (err) {
        // If parsing fails, continue with API call
        console.warn('Failed to parse cached data:', err);
      }
    }

    try {
      setLoading(true);
      // Always fetch all customers (no category filter) since we do filtering on frontend
      const response = await customerService.getAllCustomersForPSE(
        organizationId,
        accessToken,
        setUserAndToken
      );
      
      setAllCustomers(response.customers || []);
      setError('');
      
      // Cache the response
      localStorage.setItem(cacheKey, JSON.stringify(response.customers || []));
      localStorage.setItem(`${cacheKey}_timestamp`, now.toString());
    } catch (err: any) {
      console.error('API error:', err);
      setError(err.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available customer categories
  const fetchCustomerCategories = async () => {
    if (!organizationId || !accessToken) return;

    try {
      setCategoriesLoading(true);
      const response = await customerService.getCustomerCategories(organizationId, accessToken, setUserAndToken);
      setAvailableCategories(response.categories);
    } catch (err: any) {
      console.error('Error fetching customer categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Filter customers by category and search query
  useEffect(() => {
    let filtered = allCustomers;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(customer => {
        const category = getCustomerInsightCategory(customer);
        return category === selectedCategory;
      });
    }

    // Filter by search query (name search)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(customer => 
        customer.name.toLowerCase().includes(query)
      );
    }

    setFilteredCustomers(filtered);
    // Reset display count when filters change
    setDisplayCount(CUSTOMERS_PAGE_SIZE);
  }, [allCustomers, selectedCategory, searchQuery]);

  // Update displayed customers based on display count
  useEffect(() => {
    setDisplayedCustomers(filteredCustomers.slice(0, displayCount));
  }, [filteredCustomers, displayCount]);

  // Load notes count for a customer - now using embedded note_count from customer object
  const loadCustomerNotesCount = async (customerId: string) => {
    // Find the customer in the current data and use its embedded note_count
    const customer = allCustomers.find(c => c.id === customerId);
    if (customer && 'note_count' in customer) {
      setCustomerNotesCount(prev => ({
        ...prev,
        [customerId]: customer.note_count || 0
      }));
    }
  };

  const handleOpenReliabilityModal = (customer: ActiveCustomer) => {
    const analysis = getLatestAnalysis(customer.agent_analyses, 'payment_reliability_score');
    if (analysis) {
      setSelectedCustomer(customer);
      setSelectedAnalysis(analysis);
      setIsModalOpen(true);
    }
  };

  const pollReAnalyzeJobStatus = useCallback(async (jobId: string, xeroContactId: string) => {
    try {
      const result = await organizationService.getJobStatus(jobId, accessToken, setUserAndToken);

      setReAnalyzeJobStatuses(prev => ({
        ...prev,
        [xeroContactId]: result.status
      }));

      // Update localStorage with current status
      localStorage.setItem(`reanalyze_status_${organizationId}_${xeroContactId}`, result.status);

      // If job is completed or failed, stop polling and clean up
      if (['finished', 'failed'].includes(result.status)) {
        if (result.status === 'finished') {
          // Refresh customer data to show new analyses
          fetchCustomers(true);
        } else if (result.status === 'failed') {
          setAnalyzeError(`Re-analysis failed for customer ${xeroContactId}. Please try again.`);
        }
        // Immediately clear job tracking and localStorage
        setReAnalyzingCustomers(prev => {
          const newSet = new Set(prev);
          newSet.delete(xeroContactId);
          return newSet;
        });
        setReAnalyzeJobIds(prev => {
          const newIds = { ...prev };
          delete newIds[xeroContactId];
          return newIds;
        });
        setReAnalyzeJobStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[xeroContactId];
          return newStatuses;
        });
        localStorage.removeItem(`reanalyze_job_${organizationId}_${xeroContactId}`);
        localStorage.removeItem(`reanalyze_status_${organizationId}_${xeroContactId}`);
      } else {
        // Continue polling if job is still running
        setTimeout(() => pollReAnalyzeJobStatus(jobId, xeroContactId), 5000);
      }
    } catch (err) {
      console.error('Error polling re-analyze job status:', err);
      // Stop polling on error and clean up
      setReAnalyzingCustomers(prev => {
        const newSet = new Set(prev);
        newSet.delete(xeroContactId);
        return newSet;
      });
      setReAnalyzeJobIds(prev => {
        const newIds = { ...prev };
        delete newIds[xeroContactId];
        return newIds;
      });
      setReAnalyzeJobStatuses(prev => {
        const newStatuses = { ...prev };
        delete newStatuses[xeroContactId];
        return newStatuses;
      });
      localStorage.removeItem(`reanalyze_job_${organizationId}_${xeroContactId}`);
      localStorage.removeItem(`reanalyze_status_${organizationId}_${xeroContactId}`);
    }
  }, [organizationId, accessToken, setUserAndToken]);

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const result = await organizationService.getJobStatus(jobId, accessToken, setUserAndToken);

      setJobStatus(result.status);

      // Update localStorage with current status
      localStorage.setItem(`customer_analysis_status_${organizationId}`, result.status);

      // If job is completed or failed, stop polling and clean up
      if (['finished', 'failed'].includes(result.status)) {
        if (result.status === 'finished') {
          // Refresh customer data to show new analyses
          fetchCustomers(true);
        } else if (result.status === 'failed') {
          setAnalyzeError('Customer analysis job failed. Please try again.');
        }
        // Clear job tracking and localStorage after showing result
        setTimeout(() => {
          setJobId(null);
          setJobStatus(null);
          localStorage.removeItem(`customer_analysis_job_${organizationId}`);
          localStorage.removeItem(`customer_analysis_status_${organizationId}`);
        }, 5000);
      } else {
        // Continue polling if job is still running
        setTimeout(() => pollJobStatus(jobId), 5000);
      }
    } catch (err) {
      console.error('Error polling job status:', err);
      // Stop polling on error and clean up
      setJobId(null);
      setJobStatus(null);
      localStorage.removeItem(`customer_analysis_job_${organizationId}`);
      localStorage.removeItem(`customer_analysis_status_${organizationId}`);
    }
  }, [organizationId, accessToken, setUserAndToken]);

  // Check for existing customer analysis jobs on component mount
  useEffect(() => {
    const existingJobId = localStorage.getItem(`customer_analysis_job_${organizationId}`);
    const existingJobStatus = localStorage.getItem(`customer_analysis_status_${organizationId}`);

    if (existingJobId && existingJobStatus) {
      setJobId(existingJobId);
      setJobStatus(existingJobStatus);

      // If job is still running, resume polling
      if (!['finished', 'failed'].includes(existingJobStatus)) {
        pollJobStatus(existingJobId);
      }
    }

    // Check for existing re-analyze jobs for each customer
    allCustomers.forEach(customer => {
      const existingReAnalyzeJobId = localStorage.getItem(`reanalyze_job_${organizationId}_${customer.xero_contact_id}`);
      const existingReAnalyzeJobStatus = localStorage.getItem(`reanalyze_status_${organizationId}_${customer.xero_contact_id}`);

      if (existingReAnalyzeJobId && existingReAnalyzeJobStatus) {
        setReAnalyzeJobIds(prev => ({
          ...prev,
          [customer.xero_contact_id]: existingReAnalyzeJobId
        }));
        setReAnalyzeJobStatuses(prev => ({
          ...prev,
          [customer.xero_contact_id]: existingReAnalyzeJobStatus
        }));

        // If job is still running, resume polling
        if (!['finished', 'failed'].includes(existingReAnalyzeJobStatus)) {
          setReAnalyzingCustomers(prev => new Set(prev).add(customer.xero_contact_id));
          pollReAnalyzeJobStatus(existingReAnalyzeJobId, customer.xero_contact_id);
        }
      }
    });

    // Load notes count for all customers
    allCustomers.forEach(customer => {
      loadCustomerNotesCount(customer.id);
    });
  }, [organizationId, pollJobStatus, pollReAnalyzeJobStatus, allCustomers]);

  // Fetch customers and categories on mount
  useEffect(() => {
    if (organizationId && accessToken) {
      fetchCustomers();
      fetchCustomerCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, accessToken]);

  // Note: Category filtering is done in the useEffect that filters customers

  const handleReAnalyzeCustomer = async (xeroContactId: string) => {
    setReAnalyzingCustomers(prev => new Set(prev).add(xeroContactId));
    setAnalyzeError('');

    try {
      const result = await organizationService.analyzeSpecificCustomer(
        organizationId,
        xeroContactId,
        accessToken,
        setUserAndToken
      );

      if (result.job_id) {
        setReAnalyzeJobIds(prev => ({
          ...prev,
          [xeroContactId]: result.job_id
        }));
        setReAnalyzeJobStatuses(prev => ({
          ...prev,
          [xeroContactId]: 'queued'
        }));

        // Save job info to localStorage for persistence
        localStorage.setItem(`reanalyze_job_${organizationId}_${xeroContactId}`, result.job_id);
        localStorage.setItem(`reanalyze_status_${organizationId}_${xeroContactId}`, 'queued');

        // Start polling for job status
        pollReAnalyzeJobStatus(result.job_id, xeroContactId);
      }

    } catch (err: any) {
      setAnalyzeError(err.message || 'Failed to re-analyze customer');
      setReAnalyzingCustomers(prev => {
        const newSet = new Set(prev);
        newSet.delete(xeroContactId);
        return newSet;
      });
    }
  };

  const handleAnalyzeCustomers = async () => {
    setAnalyzingCustomers(true);
    setAnalyzeError('');

    try {
      const result = await organizationService.analyzeCustomers(
        organizationId,
        accessToken,
        setUserAndToken
      );

      if (result.job_id) {
        setJobId(result.job_id);
        setJobStatus('queued');

        // Save job info to localStorage for persistence
        localStorage.setItem(`customer_analysis_job_${organizationId}`, result.job_id);
        localStorage.setItem(`customer_analysis_status_${organizationId}`, 'queued');

        // Start polling for job status
        pollJobStatus(result.job_id);
      }

    } catch (err: any) {
      setAnalyzeError(err.message || 'Failed to analyze customers');
    } finally {
      setAnalyzingCustomers(false);
    }
  };

  // Get the latest analyzed timestamp from all customers
  const getLatestAnalysisTimestamp = useMemo((): Date | null => {
    let latestTimestamp: Date | null = null;

    allCustomers.forEach(customer => {
      customer.agent_analyses.forEach(analysis => {
        if (analysis.analyzed_at) {
          const analysisDate = new Date(analysis.analyzed_at);
          if (!latestTimestamp || analysisDate > latestTimestamp) {
            latestTimestamp = analysisDate;
          }
        }
      });
    });

    return latestTimestamp;
  }, [allCustomers]);

  return (
    <div className="px-6 pb-6">
      <div className="space-y-4 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900">All Customers</h3>
            <span className="text-sm text-gray-500">
              Showing {displayedCustomers.length} of {filteredCustomers.length} {filteredCustomers.length !== allCustomers.length && `(${allCustomers.length} total)`}
            </span>
          </div>

          {/* Search, Category Filter and Analyze Button */}
          <div className="flex items-center space-x-4">
            {/* Search by Name */}
            <div className="flex items-center space-x-2">
              <label htmlFor="search-customers" className="text-sm font-medium text-gray-700">
                Search:
              </label>
              <input
                id="search-customers"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name..."
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center space-x-2">
              <label htmlFor="category-filter" className="text-sm font-medium text-gray-700">
                Filter by Category:
              </label>
              <div className="relative">
                <select
                  id="category-filter"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={categoriesLoading}
                  className="px-3 py-1.5 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="All">
                    {categoriesLoading ? 'Loading categories...' : 'All Categories'}
                  </option>
                  {availableCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                {/* Custom dropdown arrow */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Analyze Customers Button with Timestamp */}
            <div className="flex flex-col items-end mt-4">
              <button
                onClick={handleAnalyzeCustomers}
                disabled={Boolean(analyzingCustomers || allCustomers.length === 0 || (jobId && jobStatus && !['finished', 'failed'].includes(jobStatus)))}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${Boolean(analyzingCustomers || allCustomers.length === 0 || (jobId && jobStatus && !['finished', 'failed'].includes(jobStatus)))
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
              >
                {analyzingCustomers ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Queuing...
                  </div>
                ) : jobId && jobStatus ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                    {jobStatus === 'queued' ? 'Queued' :
                      jobStatus === 'started' ? 'Processing...' :
                        jobStatus === 'finished' ? 'Completed' :
                          jobStatus === 'failed' ? 'Failed' : jobStatus}
                  </div>
                ) : (
                  'Analyze Customers'
                )}
              </button>

              {/* Latest Analysis Timestamp - directly below button */}
              {getLatestAnalysisTimestamp ? (
                <div className="text-right text-gray-500 text-[9px] mt-1">
                  Last analyzed: {getLatestAnalysisTimestamp.toLocaleString()}
                </div>
              ) : (
                <div className="text-right text-red-500 text-[9px] mt-1">
                  Haven't analyzed any customers yet.
                </div>
              )}
            </div>
          </div>
        </div>

        {error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        ) : null}

        {analyzeError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {analyzeError}
          </div>
        )}

        {!error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading && allCustomers.length === 0 ? (
              <div className="col-span-full flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-gray-600">Loading customers...</span>
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div className="text-gray-500 text-lg mb-2">
                  {selectedCategory === 'All' ? 'No customers found' : `No customers found in "${selectedCategory}" category`}
                </div>
                <div className="text-gray-400 text-sm">
                  {selectedCategory === 'All'
                    ? 'Customers will appear here once Xero data is synced.'
                    : 'Try selecting a different category or "All Categories".'
                  }
                </div>
              </div>
            ) : (
              displayedCustomers.map((customer) => {
                return (
                  <CustomerCard
                    key={customer.id}
                    customer={customer}
                    organizationId={organizationId}
                    reAnalyzingCustomers={reAnalyzingCustomers}
                    onReAnalyze={handleReAnalyzeCustomer}
                    onOpenPointOfContactModal={handleOpenPointOfContactModal}
                    onOpenNotesModal={handleOpenNotesModal}
                    onOpenInvoicesModal={handleOpenInvoicesModal}
                    onOpenReasoningModal={handleOpenReasoningModal}
                    onOpenReliabilityModal={handleOpenReliabilityModal}
                    onOpenAPArchetypesModal={handleOpenAPArchetypesModal}
                    onOpenGovernanceOverlaysModal={handleOpenGovernanceOverlaysModal}
                    onConfirmDisconnectAPArchetype={handleConfirmDisconnectAPArchetype}
                    onConfirmDisconnectGovernanceOverlay={handleConfirmDisconnectGovernanceOverlay}
                    onOpenCustomerCreditModal={handleOpenCustomerCreditModal}
                    hasIndividualizedCredit={hasIndividualizedCredit}
                    onOpenCustomerCreditView={handleOpenCustomerCreditView}
                  />
                );
              })
            )}
          </div>
        )}

        {/* Load More Button - only show when not searching */}
        {!error && filteredCustomers.length > displayCount && !searchQuery.trim() && (
          <div className="mt-6 flex flex-col items-center">
            <button
              onClick={() => setDisplayCount(prev => prev + CUSTOMERS_PAGE_SIZE)}
              className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
            >
              Load More Customers ({filteredCustomers.length - displayCount} remaining)
            </button>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              onClick={() => fetchCustomers(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* Customer Reasoning Modal */}
      {isModalOpen && selectedCustomer && selectedAnalysis && (
        <CustomerReasoningModal
          isOpen={isModalOpen}
          onClose={handleCloseReasoningModal}
          customer={selectedCustomer}
          analysis={selectedAnalysis}
        />
      )}

      {/* Customer Invoices Modal */}
      {isInvoicesModalOpen && selectedCustomerForInvoices && (
        <CustomerInvoicesModal
          isOpen={isInvoicesModalOpen}
          onClose={handleCloseInvoicesModal}
          customer={selectedCustomerForInvoices}
          invoices={invoicesCache[selectedCustomerForInvoices.xero_contact_id] || []}
          organizationId={organizationId}
        />
      )}

      {/* Customer Credit Terms/Limit Modal */}
      {showCreditModal && creditModalCustomer && (
        <CustomerCreditTermsLimitModal
          isOpen={showCreditModal}
          onClose={() => setShowCreditModal(false)}
          organizationId={organizationId}
          xeroDirectContactId={creditModalCustomer.id}
          accessToken={accessToken}
          setUserAndToken={setUserAndToken}
          onSaved={(res: { status: string }) => {
            setCustomerIdToHasIndividualized(prev => ({
              ...prev,
              [creditModalCustomer.id]: res.status === 'active'
            }));
            fetchCustomers(true);
          }}
        />
      )}

      {showCreditViewModal && creditViewCustomer && creditViewInitialValues && (
        <CustomerCreditTermsLimitModal
          isOpen={showCreditViewModal}
          onClose={() => setShowCreditViewModal(false)}
          organizationId={organizationId}
          xeroDirectContactId={creditViewCustomer.id}
          accessToken={accessToken}
          setUserAndToken={setUserAndToken}
          readOnly={true}
          initialValues={creditViewInitialValues}
        />
      )}

      {/* Point of Contact Input Modal */}
      {isPointOfContactModalOpen && selectedCustomerForConversation && (
        <PointOfContactInputModal
          isOpen={isPointOfContactModalOpen}
          customer={selectedCustomerForConversation}
          channel={conversationChannel}
          onChannelChange={setConversationChannel}
          showChannelSelection={true}
          picName={picName}
          picPhoneNumber={picPhoneNumber}
          picEmail={picEmail}
          countryCode={countryCode}
          conversationLoading={conversationLoading}
          conversationError={conversationError}
          existingConversations={existingConversations}
          selectedConversationId={selectedConversationId}
          onSelectedConversationIdChange={setSelectedConversationId}
          useExistingConversation={useExistingConversation}
          onClose={handleClosePointOfContactModal}
          onPicNameChange={setPicName}
          onPicPhoneNumberChange={setPicPhoneNumber}
          onPicEmailChange={setPicEmail}
          onCountryCodeChange={setCountryCode}
          onUseExistingConversationChange={setUseExistingConversation}
          onStartConversation={handleStartConversation}
        />
      )}

      {/* Notes Modal */}
      {showNotesModal && selectedCustomerForNotes && (
        <NotesModal
          isOpen={showNotesModal}
          onClose={handleCloseNotesModal}
          entityType="customer"
          entityId={selectedCustomerForNotes.id}
          entityName={selectedCustomerForNotes.name}
          organizationId={organizationId}
          onNoteCountChange={(delta: number) => {
            // Update local state
            setCustomerNotesCount(prev => ({
              ...prev,
              [selectedCustomerForNotes.id]: Math.max(0, (prev[selectedCustomerForNotes.id] || 0) + delta)
            }));
            // Update customer data cache
            setAllCustomers(prev => {
              const updatedCustomers = prev.map(customer => 
                customer.id === selectedCustomerForNotes.id 
                  ? { ...customer, note_count: Math.max(0, (customer.note_count || 0) + delta) }
                  : customer
              );
              
              // Update localStorage cache
              const cacheKey = `customers_all_${organizationId}`;
              localStorage.setItem(cacheKey, JSON.stringify(updatedCustomers));
              
              return updatedCustomers;
            });
            // Update parent customer data cache
            if (onCustomerNoteCountChange) {
              onCustomerNoteCountChange(selectedCustomerForNotes.id, delta);
            }
          }}
        />
      )}

      {/* AP Archetypes Assignment Modal */}
      {showAPArchetypesAssignmentModal && selectedCustomerForAPArchetypes && (
        <APArchetypesAssignmentModal
          isOpen={showAPArchetypesAssignmentModal}
          customer={selectedCustomerForAPArchetypes}
          availableAPArchetypes={availableAPArchetypes}
          selectedAPArchetypes={selectedAPArchetypes}
          assignmentLoading={assignmentLoading}
          assignmentError={assignmentError}
          onClose={handleCloseAPArchetypesAssignmentModal}
          onToggleArchetype={handleAPArchetypeToggle}
          onSubmit={handleSubmitAPArchetypesAssignment}
        />
      )}

      {/* Governance Overlays Assignment Modal */}
      {showGovernanceOverlaysAssignmentModal && selectedCustomerForGovernanceOverlays && (
        <GovernanceOverlaysAssignmentModal
          isOpen={showGovernanceOverlaysAssignmentModal}
          customer={selectedCustomerForGovernanceOverlays}
          availableGovernanceOverlays={availableGovernanceOverlays}
          selectedGovernanceOverlays={selectedGovernanceOverlays}
          assignmentLoading={assignmentLoading}
          assignmentError={assignmentError}
          onClose={handleCloseGovernanceOverlaysAssignmentModal}
          onToggleOverlay={handleGovernanceOverlayToggle}
          onSubmit={handleSubmitGovernanceOverlaysAssignment}
        />
      )}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectConfirmModal && disconnectItem && (
        <DisconnectConfirmationModal
          isOpen={showDisconnectConfirmModal}
          disconnectItem={disconnectItem}
          onClose={handleCloseDisconnectConfirmModal}
          onConfirm={handleConfirmDisconnect}
        />
      )}
    </div>
  );
};

export default CustomersTab;
